import i18n from 'i18next';
import {initReactI18next} from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import moment from 'moment';
import 'moment/locale/vi';

import {BASE_NAMESPACES, DEFAULT_NAMESPACE} from './namespaces';
import {
  DEFAULT_LANGUAGE,
  I18N_ENABLED,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
} from './constants';

const toLanguageOnly = (lng) => (lng || DEFAULT_LANGUAGE).split('-')[0];

const syncDocumentLanguage = (lng) => {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', lng);
  }
};

const loadPath = `${process.env.PUBLIC_URL || ''}/locales/{{lng}}/{{ns}}.json`;

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // When i18n is disabled, pin the language so browser/localStorage detection
    // cannot switch the app away from Vietnamese.
    lng: I18N_ENABLED ? undefined : DEFAULT_LANGUAGE,
    supportedLngs: I18N_ENABLED ? SUPPORTED_LANGUAGES : [DEFAULT_LANGUAGE],
    fallbackLng: DEFAULT_LANGUAGE,
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    ns: BASE_NAMESPACES,
    defaultNS: DEFAULT_NAMESPACE,
    fallbackNS: DEFAULT_NAMESPACE,
    keySeparator: '.',
    nsSeparator: ':',
    interpolation: {escapeValue: false},
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    backend: {loadPath},
    react: {useSuspense: false},
  });

// Keep moment and <html lang> aligned with the active language.
i18n.on('languageChanged', (lng) => {
  moment.locale(toLanguageOnly(lng));
  syncDocumentLanguage(toLanguageOnly(lng));
});

const initialLanguage = toLanguageOnly(i18n.language);
moment.locale(initialLanguage);
syncDocumentLanguage(initialLanguage);

// Plain helper for code that is not a component (or for bulk-migrated legacy
// code). Reactivity is provided by I18nSubscriber at the app root.
export const translate = (key, options) => {
  const ns =
    (options && options.ns) ||
    (typeof key === 'string' && key.indexOf(':') !== -1 ? key.split(':')[0] : null);
  if (ns && !i18n.hasLoadedNamespace(ns)) {
    i18n.loadNamespaces(ns);
  }
  return i18n.t(key, options);
};

export default i18n;
